import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Zap,
  Loader2,
} from 'lucide-react';
import { ApiError, publicApi, type CompanySignupBody, type MasterDataItem } from '../lib/apiClient';
import { useMasterData } from '../context/MasterDataContext';
import './HiringIntakeForm.css';

interface HiringIntakeFormProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IntakeData {
  region: string;
  companyName: string;
  contactName: string;
  workEmail: string;
  roleTitle: string;
  department: string;
  location: string;
  remotePreference: string;
  budget: string;
  topSkills: string[];
  roleDescription: string;
  priority: 'low' | 'medium' | 'high';
}

/**
 * The form region IDs map to backend region slugs. Cities are fetched from
 * `/public/cities` via the MasterDataContext cache when a region is selected, so
 * newly added cities show up without a code change.
 */
const REGIONS = [
  { id: 'us', slug: 'usa', name: 'United States', currency: 'USD ($)', symbol: '$' },
  { id: 'ca', slug: 'canada', name: 'Canada', currency: 'CAD ($)', symbol: '$' },
  { id: 'in', slug: 'india', name: 'India', currency: 'INR (₹)', symbol: '₹' },
  { id: 'th', slug: 'thailand', name: 'Thailand', currency: 'THB (฿)', symbol: '฿' },
] as const;

const INITIAL_DATA: IntakeData = {
  region: 'in',
  companyName: '',
  contactName: '',
  workEmail: '',
  roleTitle: '',
  department: '',
  location: '',
  remotePreference: 'Hybrid',
  budget: '',
  topSkills: [],
  roleDescription: '',
  priority: 'medium',
};

// Domains we reject for "work email". Personal-mail providers — kept short and lowercase.
const FREE_EMAIL_PROVIDERS = new Set([
  'gmail.com',
  'yahoo.com',
  'yahoo.co.in',
  'yahoo.co.uk',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'aol.com',
  'icloud.com',
  'me.com',
  'proton.me',
  'protonmail.com',
  'mail.com',
  'gmx.com',
  'yandex.com',
]);

type ValidatedField =
  | 'companyName'
  | 'contactName'
  | 'workEmail'
  | 'roleTitle'
  | 'department'
  | 'location'
  | 'budget';

function validateCompanyName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Company name is required';
  if (trimmed.length < 2) return 'Company name must be at least 2 characters';
  if (trimmed.length > 100) return 'Company name must be less than 100 characters';
  if (!/[a-zA-Z]/.test(trimmed)) return 'Company name must contain at least one letter';
  if (!/^[a-zA-Z0-9\s&.,'\-/]+$/.test(trimmed)) {
    return "Company name can only contain letters, numbers, spaces, and . , & - ' /";
  }
  return null;
}

function validateContactName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Name is required';
  if (trimmed.length < 2) return 'Name must be at least 2 characters';
  if (trimmed.length > 100) return 'Name must be less than 100 characters';
  if (!/[a-zA-Z]/.test(trimmed)) return 'Name must contain at least one letter';
  if (!/^[a-zA-Z\s.'-]+$/.test(trimmed)) {
    return "Name can only contain letters, spaces, and . ' -";
  }
  return null;
}

function validateWorkEmail(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return 'Work email is required';
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) return 'Please enter a valid email address';
  const domain = trimmed.split('@')[1];
  if (FREE_EMAIL_PROVIDERS.has(domain)) {
    return 'Please use a work email address (not a personal email provider)';
  }
  return null;
}

function validateDepartment(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Department is required';
  if (trimmed.length < 2) return 'Department must be at least 2 characters';
  if (trimmed.length > 50) return 'Department must be less than 50 characters';
  if (!/[a-zA-Z]/.test(trimmed)) return 'Department must contain at least one letter';
  if (!/^[a-zA-Z0-9\s&./-]+$/.test(trimmed)) {
    return 'Department can only contain letters, numbers, spaces, and & . / -';
  }
  return null;
}

function validateBudget(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Budget is required';
  if (trimmed.length > 50) return 'Budget must be less than 50 characters';
  if (!/\d/.test(trimmed)) return 'Budget must contain at least one number';
  if (!/^[a-zA-Z0-9\s-]+$/.test(trimmed)) {
    return 'Budget can only contain letters, numbers, spaces, and hyphens';
  }
  return null;
}

function validateRoleTitle(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Role title is required';
  return null;
}

function validateLocation(value: string): string | null {
  if (!value) return 'Please select a city';
  return null;
}

const ALLOWED_SKILL_CHARS = /^[a-zA-Z0-9\s.+\-/#&]+$/;

/** Validate a single skill name being added to the chip picker. */
function validateSkill(value: string, existing: string[]): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Enter a skill name.';
  if (trimmed.length > 50) return 'Skill must be 50 characters or fewer.';
  if (!/[a-zA-Z]/.test(trimmed)) return 'Skill must contain at least one letter.';
  if (!ALLOWED_SKILL_CHARS.test(trimmed)) {
    return 'Skill can only contain letters, numbers, spaces, and . + - / # &';
  }
  if (existing.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
    return 'Skill already added.';
  }
  return null;
}

function runValidator(field: ValidatedField, value: string): string | null {
  switch (field) {
    case 'companyName':
      return validateCompanyName(value);
    case 'contactName':
      return validateContactName(value);
    case 'workEmail':
      return validateWorkEmail(value);
    case 'roleTitle':
      return validateRoleTitle(value);
    case 'department':
      return validateDepartment(value);
    case 'location':
      return validateLocation(value);
    case 'budget':
      return validateBudget(value);
  }
}

const FIELDS_BY_STEP: Record<1 | 2 | 3, ValidatedField[]> = {
  1: ['companyName', 'contactName', 'workEmail'],
  2: ['roleTitle', 'department', 'location'],
  3: ['budget'],
};

type FieldErrors = Partial<Record<ValidatedField, string | null>>;

export const HiringIntakeForm: React.FC<HiringIntakeFormProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<IntakeData>(INITIAL_DATA);
  const [skillInput, setSkillInput] = useState('');
  const [skillError, setSkillError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const fieldRefs = useRef<Record<ValidatedField, HTMLElement | null>>({
    companyName: null,
    contactName: null,
    workEmail: null,
    roleTitle: null,
    department: null,
    location: null,
    budget: null,
  });
  const { loadCities } = useMasterData();
  const [cities, setCities] = useState<MasterDataItem[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const updateFormData = <K extends keyof IntakeData>(field: K, value: IntakeData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear an existing error as the user starts editing — don't keep stale errors visible.
    if (field in errors) {
      setErrors((prev) => ({ ...prev, [field as ValidatedField]: null }));
    }
  };

  const handleBlur = (field: ValidatedField, value: string) => {
    setErrors((prev) => ({ ...prev, [field]: runValidator(field, value) }));
  };

  const validateStep = (step: 1 | 2 | 3): { ok: boolean; firstError: ValidatedField | null } => {
    const next: FieldErrors = { ...errors };
    let firstError: ValidatedField | null = null;
    for (const field of FIELDS_BY_STEP[step]) {
      const err = runValidator(
        field,
        (formData as unknown as Record<string, unknown>)[field] as string,
      );
      next[field] = err;
      if (err && !firstError) firstError = field;
    }
    setErrors(next);
    return { ok: !firstError, firstError };
  };

  const scrollToField = (field: ValidatedField) => {
    const el = fieldRefs.current[field];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  };

  const handleNext = () => {
    const { ok, firstError } = validateStep(currentStep as 1 | 2 | 3);
    if (!ok) {
      if (firstError) scrollToField(firstError);
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };
  const handlePrev = () => setCurrentStep((prev) => prev - 1);

  const addSkill = () => {
    const err = validateSkill(skillInput, formData.topSkills);
    if (err) {
      setSkillError(err);
      return;
    }
    updateFormData('topSkills', [...formData.topSkills, skillInput.trim()]);
    setSkillInput('');
    setSkillError(null);
  };

  const removeSkill = (skill: string) => {
    updateFormData(
      'topSkills',
      formData.topSkills.filter((s) => s !== skill),
    );
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return Boolean(formData.companyName && formData.contactName && formData.workEmail);
      case 2:
        return Boolean(formData.roleTitle && formData.department && formData.location);
      case 3:
        return formData.topSkills.length > 0 && Boolean(formData.budget);
      default:
        return true;
    }
  };

  const progress = (currentStep / 3) * 100;
  const selectedRegion = REGIONS.find((r) => r.id === formData.region) || REGIONS[0];

  useEffect(() => {
    let cancelled = false;
    setCities([]);
    setCitiesLoading(true);
    loadCities(selectedRegion.slug)
      .then((rows) => {
        if (cancelled) return;
        setCities(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setCities([]);
      })
      .finally(() => {
        if (!cancelled) setCitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRegion.slug, loadCities]);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Phase 6.1 — wires to `POST /public/companies/signup`. The intake form collects
   * role-specific signals (title, department, skills, budget, work model) that don't have
   * direct columns on the `companies` table; we serialise them into the company description
   * field so the admin reviewer sees the original ask. The admin can flesh out the canonical
   * profile fields when the company is approved.
   */
  const handleSubmit = async () => {
    // Re-validate everything before submitting; users can land on step 3 with an unfocused
    // upstream field that turned invalid (e.g., they edited it after passing step 1).
    let firstError: ValidatedField | null = null;
    const allErrors: FieldErrors = {};
    (Object.keys(FIELDS_BY_STEP).map(Number) as Array<keyof typeof FIELDS_BY_STEP>).forEach(
      (stepKey) => {
        for (const field of FIELDS_BY_STEP[stepKey]) {
          const err = runValidator(
            field,
            (formData as unknown as Record<string, unknown>)[field] as string,
          );
          allErrors[field] = err;
          if (err && !firstError) firstError = field;
        }
      },
    );
    setErrors(allErrors);
    if (firstError) {
      // Jump to the step that owns the first invalid field so the user can see the error.
      const owningStep = (Object.entries(FIELDS_BY_STEP) as Array<[string, ValidatedField[]]>).find(
        ([, fields]) => fields.includes(firstError as ValidatedField),
      )?.[0];
      if (owningStep) setCurrentStep(Number(owningStep));
      // Defer scroll until the new step has rendered.
      setTimeout(() => scrollToField(firstError as ValidatedField), 50);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const region = REGIONS.find((r) => r.id === formData.region) ?? REGIONS[0];
      const descriptionParts: string[] = [];
      if (formData.roleTitle) descriptionParts.push(`First role: ${formData.roleTitle}`);
      if (formData.department) descriptionParts.push(`Department: ${formData.department}`);
      const cityName = cities.find((c) => c.id === formData.location)?.name ?? '';
      if (cityName) descriptionParts.push(`Location: ${cityName}, ${region.name}`);
      if (formData.remotePreference)
        descriptionParts.push(`Work model: ${formData.remotePreference}`);
      if (formData.topSkills.length)
        descriptionParts.push(`Key skills: ${formData.topSkills.join(', ')}`);
      if (formData.roleDescription) descriptionParts.push(`Notes: ${formData.roleDescription}`);

      const payload: CompanySignupBody = {
        company: {
          name: formData.companyName.trim(),
          region_slugs: [region.slug],
          description: descriptionParts.join('\n') || undefined,
          estimated_hiring_volume: formData.budget
            ? `Budget: ${region.symbol}${formData.budget}`
            : undefined,
        },
        admin: {
          full_name: formData.contactName.trim(),
          email: formData.workEmail.trim().toLowerCase(),
        },
        consent: { accepted_privacy_policy: true, accepted_terms: true },
      };
      await publicApi.submitCompanySignup(payload);
      setIsSubmitted(true);
    } catch (err: unknown) {
      if (err instanceof ApiError) setSubmitError(err.message);
      else setSubmitError('Could not submit your request. Please try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (isSubmitted) {
    return (
      <div className="qh-hiring-intake fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-brand-primary/20 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl p-12 text-center"
        >
          <button
            onClick={onClose}
            className="absolute top-8 right-8 p-2 hover:bg-gray-50 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-300" />
          </button>
          <div className="w-20 h-20 bg-brand-primary/5 rounded-full flex items-center justify-center mx-auto mb-8">
            <Sparkles className="w-10 h-10 text-brand-primary" />
          </div>
          <h3 className="text-3xl font-display font-bold text-brand-primary mb-4">Thank you</h3>
          <p className="text-brand-muted font-medium mb-10 leading-relaxed">
            We&apos;ll review your request and get back to you at{' '}
            <span className="text-brand-primary font-bold">{formData.workEmail}</span> within 1-2
            business days.
          </p>
          <button
            onClick={onClose}
            className="w-full py-4 bg-brand-primary text-white rounded-xl font-bold text-xs uppercase tracking-[0.15em] hover:brightness-110 transition-all"
          >
            Got it, thanks
          </button>
        </motion.div>
      </div>
    );
  }

  const errorClass = (field: ValidatedField) =>
    errors[field]
      ? 'border-red-400 focus:border-red-500'
      : 'border-gray-100 focus:border-brand-primary';

  const inlineError = (field: ValidatedField) =>
    errors[field] ? <p className="text-xs text-red-600 font-medium mt-1">{errors[field]}</p> : null;

  return (
    <div className="qh-hiring-intake fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-brand-primary/80 backdrop-blur-md"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 10 }}
        className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row h-full max-h-[780px]"
      >
        {/* Sidebar Info */}
        <div className="w-full md:w-72 bg-brand-secondary/30 p-10 hidden md:flex flex-col justify-between relative border-r border-gray-100">
          <div className="relative z-10">
            <div className="w-10 h-10 bg-brand-primary rounded-xl flex items-center justify-center mb-10 shadow-lg shadow-brand-primary/10">
              <Zap className="w-5 h-5 text-brand-accent fill-brand-accent" />
            </div>
            <h2 className="text-2xl font-display font-bold leading-tight mb-8 text-brand-primary">
              Precision Hiring
            </h2>
            <div className="space-y-8">
              <div className="flex gap-3 items-start">
                <div className="w-5 h-5 rounded-full bg-brand-primary/5 flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle2 className="w-3 h-3 text-brand-primary" />
                </div>
                <p className="text-xs font-bold text-brand-muted leading-relaxed">
                  Neural screening for 98% accuracy
                </p>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-5 h-5 rounded-full bg-brand-primary/5 flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle2 className="w-3 h-3 text-brand-primary" />
                </div>
                <p className="text-xs font-bold text-brand-muted leading-relaxed">
                  Personal talent partner
                </p>
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-5 h-5 rounded-full bg-brand-primary/5 flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle2 className="w-3 h-3 text-brand-primary" />
                </div>
                <p className="text-xs font-bold text-brand-muted leading-relaxed">
                  Risk-free outcome pricing
                </p>
              </div>
            </div>
          </div>

          <div className="relative z-10 pt-8 mt-auto">
            <div className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 text-brand-accent">
                Avg. TAT
              </p>
              <p className="text-lg font-display font-bold text-brand-primary">4.2 Days</p>
            </div>
          </div>
        </div>

        {/* Form Area */}
        <div className="flex-1 bg-white p-8 md:p-14 flex flex-col relative overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-10 right-10 p-2 hover:bg-gray-50 rounded-xl transition-colors z-20"
          >
            <X className="w-5 h-5 text-gray-300" />
          </button>

          {/* Progress Header */}
          <div className="mb-10">
            <div className="flex justify-between items-end mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-pulse" />
                  <span className="text-brand-muted font-bold text-[10px] uppercase tracking-[0.25em]">
                    Step {currentStep} of 3
                  </span>
                </div>
                <h3 className="text-3xl font-display font-bold text-brand-primary tracking-tight">
                  {currentStep === 1 && 'Basic Context'}
                  {currentStep === 2 && 'The Role'}
                  {currentStep === 3 && 'The Talent'}
                </h3>
              </div>
            </div>
            <div className="h-1 w-full bg-gray-50 rounded-full overflow-hidden">
              <motion.div animate={{ width: `${progress}%` }} className="h-full bg-brand-primary" />
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar overscroll-contain">
            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                  <div className="space-y-4">
                    <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted flex items-center gap-2">
                      Focus Region
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {REGIONS.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            updateFormData('region', r.id);
                            updateFormData('location', '');
                          }}
                          className={`px-4 py-3 rounded-xl text-xs font-bold border transition-all ${
                            formData.region === r.id
                              ? 'bg-brand-primary text-white border-brand-primary shadow-md'
                              : 'bg-white text-brand-muted border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                        Company Name
                      </label>
                      <input
                        type="text"
                        ref={(el) => {
                          fieldRefs.current.companyName = el;
                        }}
                        value={formData.companyName}
                        onChange={(e) => updateFormData('companyName', e.target.value)}
                        onBlur={(e) => handleBlur('companyName', e.target.value)}
                        placeholder="Acme Inc."
                        className={`w-full px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${errorClass('companyName')}`}
                      />
                      {inlineError('companyName')}
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        ref={(el) => {
                          fieldRefs.current.contactName = el;
                        }}
                        value={formData.contactName}
                        onChange={(e) => updateFormData('contactName', e.target.value)}
                        onBlur={(e) => handleBlur('contactName', e.target.value)}
                        placeholder="Jane Doe"
                        className={`w-full px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${errorClass('contactName')}`}
                      />
                      {inlineError('contactName')}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                      Work Email
                    </label>
                    <input
                      type="email"
                      ref={(el) => {
                        fieldRefs.current.workEmail = el;
                      }}
                      value={formData.workEmail}
                      onChange={(e) => updateFormData('workEmail', e.target.value)}
                      onBlur={(e) => handleBlur('workEmail', e.target.value)}
                      placeholder="name@company.com"
                      className={`w-full px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${errorClass('workEmail')}`}
                    />
                    {inlineError('workEmail')}
                  </div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                        Role Title
                      </label>
                      <input
                        type="text"
                        ref={(el) => {
                          fieldRefs.current.roleTitle = el;
                        }}
                        value={formData.roleTitle}
                        onChange={(e) => updateFormData('roleTitle', e.target.value)}
                        onBlur={(e) => handleBlur('roleTitle', e.target.value)}
                        placeholder="Product Designer"
                        className={`w-full px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${errorClass('roleTitle')}`}
                      />
                      {inlineError('roleTitle')}
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                        Department
                      </label>
                      <input
                        type="text"
                        ref={(el) => {
                          fieldRefs.current.department = el;
                        }}
                        value={formData.department}
                        onChange={(e) => updateFormData('department', e.target.value)}
                        onBlur={(e) => handleBlur('department', e.target.value)}
                        placeholder="Engineering"
                        className={`w-full px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${errorClass('department')}`}
                      />
                      {inlineError('department')}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                        Location ({selectedRegion.name})
                      </label>
                      <select
                        ref={(el) => {
                          fieldRefs.current.location = el;
                        }}
                        value={formData.location}
                        onChange={(e) => updateFormData('location', e.target.value)}
                        onBlur={(e) => handleBlur('location', e.target.value)}
                        disabled={citiesLoading}
                        className={`w-full px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-wait ${errorClass('location')}`}
                      >
                        <option value="">
                          {citiesLoading ? 'Loading cities…' : 'Select City'}
                        </option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                      {inlineError('location')}
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                        Policy
                      </label>
                      <select
                        value={formData.remotePreference}
                        onChange={(e) => updateFormData('remotePreference', e.target.value)}
                        className="w-full px-5 py-3.5 bg-white border border-gray-100 rounded-xl focus:border-brand-primary outline-none transition-all font-medium text-brand-primary text-sm shadow-sm appearance-none cursor-pointer"
                      >
                        <option>On-site</option>
                        <option>Hybrid</option>
                        <option>Fully Remote</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                  <div className="space-y-4">
                    <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                      Key Required Skills
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={skillInput}
                        onChange={(e) => {
                          setSkillInput(e.target.value);
                          if (skillError) setSkillError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addSkill();
                          }
                        }}
                        placeholder="Add skill..."
                        className={`flex-1 px-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${
                          skillError
                            ? 'border-red-400 focus:border-red-500'
                            : 'border-gray-100 focus:border-brand-primary'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={addSkill}
                        className="px-6 bg-brand-primary text-white rounded-xl hover:bg-brand-deep transition-all shadow-md font-bold text-xs uppercase"
                      >
                        Add
                      </button>
                    </div>
                    {skillError && <p className="text-xs text-red-600 font-medium">{skillError}</p>}
                    <div className="flex flex-wrap gap-2">
                      {formData.topSkills.map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1.5 bg-gray-50 text-brand-primary rounded-lg text-[11px] font-bold flex items-center gap-2 border border-gray-100"
                        >
                          {skill}
                          <button
                            onClick={() => removeSkill(skill)}
                            className="hover:text-brand-accent transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-black uppercase tracking-widest text-brand-muted">
                      Budget ({selectedRegion.currency})
                    </label>
                    <div className="relative">
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                        {selectedRegion.symbol}
                      </span>
                      <input
                        type="text"
                        ref={(el) => {
                          fieldRefs.current.budget = el;
                        }}
                        value={formData.budget}
                        onChange={(e) => updateFormData('budget', e.target.value)}
                        onBlur={(e) => handleBlur('budget', e.target.value)}
                        placeholder={`e.g. ${formData.region === 'in' ? '12L - 18L' : '100k - 140k'}`}
                        className={`w-full pl-10 pr-5 py-3.5 bg-white border rounded-xl outline-none transition-all font-medium text-brand-primary text-sm shadow-sm placeholder:text-gray-300 ${errorClass('budget')}`}
                      />
                    </div>
                    {inlineError('budget')}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Controls */}
          <div className="pt-8 mt-auto">
            {submitError && <p className="mb-3 text-sm text-red-700 font-medium">{submitError}</p>}
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  onClick={handlePrev}
                  disabled={isSubmitting}
                  className="px-6 border border-gray-100 rounded-xl font-bold hover:bg-gray-50 transition-all text-gray-500 disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}

              {currentStep === 3 ? (
                <button
                  onClick={handleSubmit}
                  disabled={!isStepValid() || isSubmitting}
                  className="flex-1 py-4 bg-brand-primary text-white rounded-xl font-bold text-xs uppercase tracking-[0.15em] hover:brightness-110 transition-all shadow-xl shadow-brand-primary/10 flex items-center justify-center gap-2 disabled:opacity-50 disabled:grayscale"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Submit Request <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="flex-1 py-4 bg-brand-primary text-white rounded-xl font-bold text-xs uppercase tracking-[0.15em] hover:bg-brand-deep transition-all shadow-xl shadow-brand-primary/10 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  Next Step <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
